import { useEffect, useMemo } from "react";
import { Sparkles } from "lucide-react";
import ProductRail from "../../components/shop/ProductRail";
import CategoryRail from "../../components/shop/CategoryRail";
import { computeAvailableSizes } from "../../auth/ShopLayout";
import { useShopItems, useShopTrending, useDepartments } from "../../lib/queries";
import { isOnSale, effectivePrice } from "../../lib/price";

import LuxuryHero from "../../components/shop/home/LuxuryHero";
import MobileCategoryCarousel from "../../components/shop/home/MobileCategoryCarousel";
import DiscoverySearch from "../../components/shop/home/DiscoverySearch";
import CustomStudioTeaser from "../../components/shop/home/CustomStudioTeaser";
import ComingSoonStrip from "../../components/shop/home/ComingSoonStrip";
import HomeDepartmentDuo from "../../components/shop/home/HomeDepartmentDuo";
import BrandStorySection from "../../components/shop/home/BrandStorySection";
import EditorialTestimonials from "../../components/shop/home/EditorialTestimonials";
import InstagramShowcase from "../../components/shop/home/InstagramShowcase";
import SaleOffersTiles from "../../components/shop/home/SaleOffersTiles";
import HomeTrustStrip from "../../components/shop/home/HomeTrustStrip";
import Seo from "../../components/seo/Seo";
// Phase 2 (Jul 2026) — removed from the homepage (components intact for reuse):
// EditorialDepartments (→ ComingSoonStrip), FeaturedCategories + DepartmentTiles
// (duplicate navigation; the category carousel + sidebar + menu carry it),
// LuxuryLookbook (its page is empty; best imagery lives in the hero).

function Skeleton() {
    return (
        <div className="space-y-9 max-w-[90rem] mx-auto" data-testid="shop-skeleton">
            {Array.from({ length: 2 }).map((_, idx) => (
                <div key={idx}>
                    <div className="h-7 w-48 skeleton rounded mb-4" />
                    <div className="flex gap-3 md:gap-4 overflow-hidden">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="flex-shrink-0 w-[44%] sm:w-[28%] md:w-[22%] lg:w-[220px] rounded-2xl border border-stone-200 overflow-hidden bg-white">
                                <div className="aspect-[3/4] skeleton" />
                                <div className="p-3 space-y-2">
                                    <div className="h-3 w-3/4 skeleton rounded" />
                                    <div className="h-3 w-1/3 skeleton rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function ShopHome() {
    const { data: items = [], isLoading: itemsLoading } = useShopItems();
    // 12 pieces feed the rail — enough depth to browse behind the arrows,
    // still a curation (View all carries the rest of the catalog).
    const { data: trending = [], isLoading: trendingLoading } = useShopTrending(12);
    const { data: departments = [] } = useDepartments();
    const loading = itemsLoading || trendingLoading;

    // Jul 2026 — Jewellery as a second vertical on the home page. The home
    // used to read as a clothing-only store; now it surfaces a dedicated
    // "New in Jewellery" rail AND a two-department discovery band. BOTH are
    // gated on the jewellery department actually being LIVE (prod stays
    // coming_soon until Sagar flips it) with real in-stock pieces — so this
    // changes nothing in production today and auto-appears at go-live.
    const jewelleryLive = useMemo(
        () => departments.some((d) => d.slug === "jewellery" && d.status === "live"),
        [departments],
    );
    const clothingLive = useMemo(
        () => departments.some((d) => d.slug === "clothing" && d.status === "live"),
        [departments],
    );
    const jewelleryItems = useMemo(
        () => items.filter((i) => i.department_slug === "jewellery" && (i.quantity || 0) > 0).slice(0, 12),
        [items],
    );
    const showJewelleryRail = jewelleryLive && jewelleryItems.length >= 2;
    // The two-vertical "worlds" band only earns its place once there's a
    // genuine second department to point at (both live).
    const showDepartmentDuo = jewelleryLive && clothingLive;

    // Broadcast filter helpers (for sidebar — sizes / max price)
    useEffect(() => {
        if (!items.length) return;
        const sizes = computeAvailableSizes(items);
        const maxPrice = items.reduce((m, i) => Math.max(m, i.selling_price || 0), 0);
        if (typeof window !== "undefined") {
            window.__jns_available_sizes = sizes;
            window.__jns_max_price = maxPrice;
        }
        window.dispatchEvent(new CustomEvent("jns:available-sizes", { detail: sizes }));
        window.dispatchEvent(new CustomEvent("jns:max-price", { detail: maxPrice }));
    }, [items]);

    // Phase 2 — ONE product rail (bestsellers = trending). New Arrivals only
    // renders with ≥6 genuinely fresh pieces NOT already in the rail, so the
    // small catalog is never recycled across sections.
    // Resilience: if /shop/trending flakes (observed live: a transient
    // status-0 abort left the homepage with ZERO products), fall back to
    // the catalog feed — one failed request must never empty the boutique.
    // Clothing-led ordering — we're a clothing boutique that also carries
    // jewellery, so on any SHARED rail we lead with clothing and append at
    // most `cap` jewellery pieces. A single big new vertical (e.g. 37 same-day
    // earrings) therefore can't flood Bestsellers or New Arrivals. A
    // clothing-only or jewellery-only feed is returned unchanged (minus trim).
    const clothingLed = (list, cap = 3, size = 12) => {
        const clo = [], jew = [];
        for (const i of list) ((i?.department_slug || "clothing") === "jewellery" ? jew : clo).push(i);
        // RESERVE up to `cap` slots for jewellery so a few pieces always show
        // (cloth-first WITH some jewellery), then clothing fills the rest — a
        // big clothing feed can't push jewellery out, nor can jewellery flood.
        const jewShow = jew.slice(0, cap);
        return [...clo.slice(0, size - jewShow.length), ...jewShow];
    };
    const railSource = trending.length > 0 ? trending : items;
    const bestsellers = clothingLed(railSource.filter((t) => t.quantity > 0), 3, 12);
    const bestsellerIds = useMemo(() => new Set(bestsellers.map((b) => b.id)), [bestsellers]);
    const newArrivals = useMemo(() => {
        const now = Date.now();
        const fresh = items.filter((i) => {
            if ((i.quantity || 0) <= 0 || bestsellerIds.has(i.id)) return false;
            const d = Date.parse(i.created_at || i.added_at || "");
            return Number.isFinite(d) && (now - d) / 86400000 <= 30;
        });
        // Same clothing-led cap so New Arrivals stays cloth-first with a few
        // fresh jewellery pieces — never a wall of same-day earrings.
        return clothingLed(fresh, 3, 12);
    }, [items, bestsellerIds]);
    // Render-gate on the ACTUAL (capped) rail, so New Arrivals hides rather
    // than showing a thin or jewellery-flooded row.
    const freshCount = newArrivals.length;

    // Sale strip earns its place only with a REAL markdown (≥10% off any
    // in-stock piece) — a wall of 5%-off badges reads cheaper than no sale.
    const saleWorthy = useMemo(() => items.some((i) => {
        if (!isOnSale(i) || (i.quantity || 0) <= 0) return false;
        const reg = Number(i.selling_price || 0);
        return reg > 0 && (reg - effectivePrice(i)) / reg >= 0.10;
    }), [items]);

    // Jul 2026 (Sagar) — "Shop by category" rails give the home more
    // shoppable surface. CLOTHING-LED MIX: we're a clothing boutique that
    // also carries jewellery, so the section leads with clothing and folds in
    // at most one jewellery category — otherwise a single big new vertical
    // (e.g. 37 Earrings at launch) sorts to the top and crowds clothing out.
    // Each rail is self-labelled + links to its /c/<name> page.
    const categoryRails = useMemo(() => {
        const byCat = {};
        for (const i of items) {
            if ((i.quantity || 0) <= 0) continue;
            const c = (i.category || "").trim();
            if (!c) continue;
            if (!byCat[c]) byCat[c] = { name: c, dept: i.department_slug || "clothing", items: [] };
            byCat[c].items.push(i);
        }
        const rank = (arr) => arr.sort((a, b) => b.items.length - a.items.length);
        const deep = Object.values(byCat).filter((g) => g.items.length >= 4);
        const clothing = rank(deep.filter((g) => g.dept !== "jewellery"));
        const jewellery = rank(deep.filter((g) => g.dept === "jewellery"));
        // Clothing brackets the one jewellery rail: [clothing, jewellery, clothing].
        const picked = [clothing[0], jewellery[0], clothing[1]].filter(Boolean);
        // Top up to 3 from remaining depth — clothing first, then jewellery —
        // so a clothing-only or jewellery-only catalogue still fills the row.
        for (const g of [...clothing.slice(2), ...jewellery.slice(1)]) {
            if (picked.length >= 3) break;
            if (!picked.includes(g)) picked.push(g);
        }
        return picked.slice(0, 3).map((g) => ({ name: g.name, items: g.items }));
    }, [items]);

    return (
        <div data-testid="shop-home-page">
            <Seo
                title="Indian Wear Online — sarees, lehengas, anarkalis & custom outfits"
                description="JnS Creation — a family-run Indian-wear boutique. Handpicked sarees, lehengas, anarkalis and kurtis, plus custom-stitched outfits with AI try-on. Pan-India shipping, COD."
                canonical="/"
            />
            {/* ── 0 · STICKY SEARCH (mobile + desktop) — always one glance away */}
            <DiscoverySearch items={items} />

            {/* ── 0.5 · CATEGORY CIRCLES — pinned to the very top so shoppers who
                "know what they want" can jump straight to a category before the
                hero or anything else (Sagar's ask, Jul 2026). */}
            <div className="mb-3 lg:mb-6" data-testid="home-category-band">
                <MobileCategoryCarousel />
            </div>

            {/* ── 1 · COMPACT HERO (55vh mobile, one CTA, trust under CTA) ── */}
            <LuxuryHero />

            {loading ? (
                <Skeleton />
            ) : (
                <>
                    {/* ── 2 · BESTSELLERS — the argument. Real purchasable
                        cards, ≥2 fully visible at 375, prices on. */}
                    {bestsellers.length > 0 && (
                        <ProductRail
                            id="bestsellers"
                            sectionTestId="trending-rail-section"
                            gridTestId="trending-grid"
                            ctrlPrefix="trending"
                            className="mb-10 lg:mb-16"
                            eyebrow="Bestsellers"
                            title={<>
                                <Sparkles size={24} className="inline -mt-2 mr-2 text-rosegold" />
                                Pieces women are <span className="italic text-wine">loving</span>.
                            </>}
                            subline={items.length > 0 && (
                                <p className="mt-2 text-[11.5px] text-ink-soft tracking-wide" data-testid="curated-count-line">
                                    {items.length} pieces in the boutique — every one hand-picked.
                                </p>
                            )}
                            viewAllTo="/c/All"
                            viewAllLabel={`View all ${items.length > 0 ? items.length : ""} pieces`}
                            items={bestsellers}
                        />
                    )}

                    {/* ── 2.5 · NEW IN JEWELLERY — the second vertical earns
                        prime real estate right under bestsellers. Gated on the
                        dept being live with real stock (invisible until the
                        prod go-live flip), so the home now reads as a genuine
                        two-vertical boutique, not a clothing-only store. */}
                    {showJewelleryRail && (
                        <ProductRail
                            id="new-in-jewellery"
                            sectionTestId="jewellery-rail-section"
                            gridTestId="jewellery-grid"
                            ctrlPrefix="jewellery"
                            className="mb-10 lg:mb-16"
                            eyebrow="New in · Jewellery"
                            title={<>Adorn the <span className="italic text-wine">look</span>.</>}
                            subline={(
                                <p className="mt-2 text-[11.5px] text-ink-soft tracking-wide" data-testid="jewellery-rail-subline">
                                    Kundan, pearls &amp; temple silhouettes — hand-picked to finish your ethnic looks.
                                </p>
                            )}
                            viewAllTo="/d/jewellery"
                            viewAllLabel="Explore jewellery"
                            items={jewelleryItems}
                        />
                    )}

                    {/* ── 4 · CUSTOM STUDIO — the differentiator's ONE moment */}
                    <CustomStudioTeaser />

                    {/* ── 5 · NEW ARRIVALS (conditional: ≥6 fresh, non-recycled)
                        Same rail treatment as Bestsellers — identical tiles,
                        arrows, view-all — so the page has ONE carousel grammar. */}
                    {freshCount >= 6 && (
                        <ProductRail
                            id="new-arrivals"
                            sectionTestId="new-arrivals-section"
                            gridTestId="new-arrivals-grid"
                            ctrlPrefix="new-arrivals"
                            className="mb-12 lg:mb-24"
                            eyebrow="Just in"
                            title={<>New <span className="italic text-wine">arrivals</span>.</>}
                            viewAllTo="/c/All?sort=newest"
                            viewAllLabel="View all pieces"
                            items={newArrivals}
                        />
                    )}

                    {/* ── 5.3 · EXPLORE THE BOUTIQUE — two-vertical "worlds"
                        band. Appears only once a second department is live, so
                        the home gains a genuine two-world identity at go-live. */}
                    {showDepartmentDuo && <HomeDepartmentDuo departments={departments} />}

                    {/* ── 5.5 · SHOP BY CATEGORY — top categories as rails so the
                        small catalog gets more shoppable surface (Sagar, Jul 2026). */}
                    {categoryRails.length > 0 && (
                        <div className="mb-4 lg:mb-10" data-testid="home-category-rails">
                            {categoryRails.map((c) => (
                                <CategoryRail key={c.name} category={c.name} items={c.items} />
                            ))}
                        </div>
                    )}

                    {/* ── 6 · SALE + COUPONS (only when a real markdown exists) */}
                    {saleWorthy && <SaleOffersTiles />}

                    {/* ── 7 · TRUST ROW ─────────────────────────────────── */}
                    <HomeTrustStrip />

                    {/* ── 8 · BRAND STORY (trimmed) ────────────────────── */}
                    <BrandStorySection />

                    {/* ── 9 · TESTIMONIALS ────────────────────────────── */}
                    <EditorialTestimonials />

                    {/* ── 10 · COMING SOON (one quiet line, keeps notify capture) */}
                    <ComingSoonStrip />

                    {/* ── 11 · INSTAGRAM ───────────────────────────────── */}
                    <InstagramShowcase />
                </>
            )}
        </div>
    );
}

